import { Box } from '@mui/material';
import { GridColDef, GridColumnGroupingModel } from '@mui/x-data-grid-premium';
import moment from 'moment';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  BooleanCellComponent,
  ButtonCellComponent,
  EmployeeCellComponent,
  conditionalGridColDefs,
  dateCellValueFormatter,
  getFieldNamesFromColumnConfig,
  getSingleSelectColumnEnumValueOptions,
} from '~/components/SolaForceDataGrid';
import { useGetEntityAttributes } from '~/data/attributes/useGetEntityAttributes';
import { useAllCostCenters } from '~/data/costCenters';
import { useAllEmployees } from '~/data/employees';
import { Enum, EnumListKey, useAllEnums } from '~/data/enums';
import { useLanguages } from '~/data/languages';
import { useOrgTree } from '~/data/orgunits';
import { useMyUnitIds } from '~/data/orgunits/useMyUnitIds';
import {
  AnalyticsReportSettingsProps,
  DynamicPersonnelIncludeOptionalData,
  DynamicPersonnelReportData,
} from '~/data/reports';
import { useTrialPeriodOptions } from '~/data/trialPeriod/useTrialPeriodOptions';
import { useRoleTranslations } from '~/data/user';
import {
  useIsEndedEmployeeViewRestricted,
  useReportNavigateToView,
} from '~/scenes/PortalModules/Analytics/common';
import {
  AdditionalCostCenter,
  CompensationEntry,
  PersonnelListCustomReportRow,
  PersonnelListCustomReportRow_AccountDetails,
  PersonnelListCustomReportRow_Contact,
  PersonnelListCustomReportRow_Employee,
  PersonnelListCustomReportRow_Employment,
  PersonnelListCustomReportRow_Misc,
  PersonnelListCustomReportRow_PersonnelGroups,
  PersonnelListCustomReportRow_Position,
  PersonnelListCustomReportRow_SecondaryTree,
  convertPersonnelListCustomReportDataToPersonnelListCustomReportRow,
} from '~/scenes/PortalModules/Analytics/People/PersonnelListCustomReport/data';
import { getLargerNumber } from '~/utils/helper';
import {
  convertToDateOrUndefined,
  getFormattedDateString,
} from '~/utils/helpers/date';
import { getEnumName, getEnumObject } from '~/utils/helpers/enum';

export type DynamicPersonnelReportSelectorCustomParams = {
  optionalData: DynamicPersonnelIncludeOptionalData[];
};

type ReportColumnWidths = {
  roles: number;
  email: number;
};

type PropsType = {
  data: DynamicPersonnelReportData[];
  reportSelection: AnalyticsReportSettingsProps<DynamicPersonnelReportSelectorCustomParams>;
};

export const usePersonnelListCustomReport = (props: PropsType) => {
  const {
    data,
    reportSelection: { params },
  } = props;

  const { reportDate, isReportDateToday } = useMemo(() => {
    const reportDate = params?.startDate;

    return {
      reportDate,
      isReportDateToday: !!reportDate
        ? moment().isSame(moment(reportDate), 'date')
        : false,
    };
  }, [params]);

  const { goToEmployeeBasicDataView } = useReportNavigateToView();

  const { t } = useTranslation();
  const { getRoleTranslation } = useRoleTranslations();
  const { getTrialPeriodOptionName } = useTrialPeriodOptions();

  const { isAccessToEndedEmployeeRestrictedByEmploymentStatus } =
    useIsEndedEmployeeViewRestricted();
  const { aMyHRUnits, aMyManUnits, aMyAltUnits } = useMyUnitIds();

  const { languages } = useLanguages();
  const getLanguageTranslation = useCallback(
    (langCode: string) => {
      return (
        languages.find((lang) => lang.fComLanguageCode === langCode)
          ?.fComLanguageName ?? langCode
      );
    },
    [languages],
  );

  const { getEnums, getCustomDataEnums } = useAllEnums();
  const {
    data: employmentDetailsEntityAttributes,
    isLoading: isLoadingEmploymentDetailsEntityAttributes,
  } = useGetEntityAttributes({
    entity: 'EMPLOYMENT_DETAILS',
  });

  const getCustomDataFieldDataType = useCallback(
    (fieldName: string) => {
      return employmentDetailsEntityAttributes.find(
        (a) => a.fCustomDataAttributeName === fieldName,
      )?.fCustomDataAttributeDataType;
    },
    [employmentDetailsEntityAttributes],
  );

  const customDataEnums = useMemo(() => {
    return Object.fromEntries(
      employmentDetailsEntityAttributes
        .filter((a) =>
          ['ENUM', 'MULTISELECT'].includes(a.fCustomDataAttributeDataType),
        )
        .map(({ fCustomDataAttributeName }) => {
          return [
            fCustomDataAttributeName,
            getCustomDataEnums(
              `__CUSTOMDATA__EMPLOYMENT_DETAILS__${fCustomDataAttributeName}`,
            ),
          ];
        }),
    );
  }, [employmentDetailsEntityAttributes, getCustomDataEnums]);
  const getCustomDataFieldEnums = useCallback(
    (fieldName: string) => {
      return customDataEnums[fieldName] ?? [];
    },
    [customDataEnums],
  );

  const { getEmployeeById } = useAllEmployees();
  const getEmployeeName = useCallback(
    (employeeId?: number): string => {
      const foundEmployee = getEmployeeById(employeeId);

      return foundEmployee
        ? `${foundEmployee.fEmpFirstName} ${foundEmployee.fEmpLastName}`
        : '';
    },
    [getEmployeeById],
  );
  const getEmployeeNames = useCallback(
    (employeeIds: number[]) => {
      return employeeIds
        .map((eId) => getEmployeeName(eId))
        .filter((name) => !!name)
        .join(', ');
    },
    [getEmployeeName],
  );

  const { orgTree } = useOrgTree();
  const getParentUnitName = useCallback(
    (unitId: number): string => {
      const primaryTreeUnits = orgTree.find(
        (tree) => tree.fTreeId === 1,
      )?.units;

      const foundUnitParentId = primaryTreeUnits?.find(
        (unit) => unit.fTreeUnitId === `${unitId}`,
      )?.fTreeParentUnitId;

      const parentId = !!foundUnitParentId
        ? parseInt(foundUnitParentId)
        : undefined;

      if (!!parentId && !isNaN(parentId)) {
        return (
          primaryTreeUnits?.find((unit) => unit.fTreeUnitId === `${parentId}`)
            ?.fTreeUnitName ?? ''
        );
      }

      return '';
    },
    [orgTree],
  );

  const { getCostCenterById } = useAllCostCenters();

  const enabledOptionalDataSections = useMemo(() => {
    const isOptionalDataEnabled = (
      dataOption: DynamicPersonnelIncludeOptionalData,
    ) => !!params?.optionalData.includes(dataOption);

    return {
      salariesDataEnabled: isOptionalDataEnabled(
        DynamicPersonnelIncludeOptionalData.Salaries,
      ),
      workingHoursDataEnabled: isOptionalDataEnabled(
        DynamicPersonnelIncludeOptionalData.WorkingHours,
      ),
      totalCompensationDataEnabled: isOptionalDataEnabled(
        DynamicPersonnelIncludeOptionalData.TotalCompensation,
      ),
      additionalPersonnelGroupsDataEnabled: isOptionalDataEnabled(
        DynamicPersonnelIncludeOptionalData.AdditionalPersonnelGroups,
      ),
      additionalTreeDataEnabled: isOptionalDataEnabled(
        DynamicPersonnelIncludeOptionalData.AdditionalTrees,
      ),
      accountDetailsDataEnabled: isOptionalDataEnabled(
        DynamicPersonnelIncludeOptionalData.UserAccountDetails,
      ),
    };
  }, [params]);

  const reportEnums = useMemo(() => {
    const customEntityEmploymentDetailsEnums = getEnums(
      EnumListKey.CUSTOM_ENTITY_EMPLOYMENT_DETAILS,
    );

    return {
      countryEnums: getEnums(EnumListKey.COUNTRY),
      employeeStatusEnums: getEnums(EnumListKey.EMPLOYEE_STATUS),
      positionEnums: getEnums(EnumListKey.EMPLOYEE_POSITION),
      employmentTypeEnums: getEnums(EnumListKey.EMPLOYMENT_TYPE),
      employmentStatusEnums: getEnums(EnumListKey.EMPLOYMENT_STATUS),
      jobGradeEnums: getEnums(EnumListKey.JOB_GRADE),
      personnelGroupNames: getEnums(EnumListKey.PERSONNEL_GROUP_NAME),
      personnelGroupEnumsByGroup: Object.fromEntries(
        [
          EnumListKey.PERSONNEL_GROUP_1,
          EnumListKey.PERSONNEL_GROUP_2,
          EnumListKey.PERSONNEL_GROUP_3,
          EnumListKey.PERSONNEL_GROUP_4,
          EnumListKey.PERSONNEL_GROUP_5,
        ].map((key) => {
          return [key, getEnums(key)];
        }),
      ),
      workingHoursTypeEnums: getEnums(EnumListKey.WORKING_HOURS_TYPE),
      salaryTypeEnums: getEnums(EnumListKey.REW_SALARY_TYPE),
      salaryClassEnums: getEnums(EnumListKey.REW_SALARY_CLASS),
      salaryChangeReasonEnums: getEnums(EnumListKey.SALARY_CHANGE_REASON),
      allowanceTypeEnums: getEnums(EnumListKey.ALLOWANCE_TYPE),
      benefitTypeEnums: getEnums(EnumListKey.BENEFIT_TYPE),
      eventFrequencyEnums: getEnums(EnumListKey.EVENT_FREQUENCY),
      fixedTermReasonEnums: getEnums(EnumListKey.EMPL_FIXED_TERM_REASON),
      customEntityEmploymentDetailsEnums,
    };
  }, [getEnums]);

  const { rows, columnWidths } = useMemo(() => {
    const rows =
      convertPersonnelListCustomReportDataToPersonnelListCustomReportRow({
        data,
        reportDate,
        getParentUnitName,
        getEmployeeById,
        getCostCenterById,
        getRoleTranslation,
        myHRUnits: aMyHRUnits,
        myManagerUnits: aMyManUnits,
        myAltManagerUnits: aMyAltUnits,
      });

    const columnContentLength = rows.reduce(
      (acc, row) => {
        return {
          roles: getLargerNumber(acc.roles, row.roles.length),
          email: getLargerNumber(acc.email, row.email.length),
        };
      },
      {
        roles: 0,
        email: 0,
      } as ReportColumnWidths,
    );

    const columnWidths = Object.fromEntries(
      Object.entries(columnContentLength).map(([key, value]) => {
        return [key, value * 8];
      }),
    ) as ReportColumnWidths;

    return {
      rows,
      columnWidths,
    };
  }, [
    data,
    reportDate,
    getParentUnitName,
    getEmployeeById,
    getCostCenterById,
    getRoleTranslation,
    aMyHRUnits,
    aMyManUnits,
    aMyAltUnits,
  ]);

  const dataColumnOptions = useMemo(() => {
    return rows.reduce(
      (acc, row) => {
        const {
          currency,
          nationality,
          status,
          employmentType,
          employmentStatus,
          jobGrade,
          reasonOfFixedTerms,
          country,
        } = row;

        const includeIfMissing = (options: string[], value: string) => {
          if (!options.includes(value)) {
            options.push(value);
          }
        };

        includeIfMissing(acc.nationalityOptions, nationality);
        includeIfMissing(acc.employeeStatusOptions, status);
        includeIfMissing(acc.employmentTypeOptions, employmentType);
        includeIfMissing(acc.employmentStatusOptions, employmentStatus);
        includeIfMissing(acc.jobGradeOptions, jobGrade);
        includeIfMissing(acc.reasonOfFixedTermsOptions, reasonOfFixedTerms);
        includeIfMissing(acc.currencyOptions, currency);
        includeIfMissing(acc.countryOptions, country);

        return acc;
      },
      {
        nationalityOptions: [],
        employeeStatusOptions: [],
        employmentTypeOptions: [],
        employmentStatusOptions: [],
        jobGradeOptions: [],
        reasonOfFixedTermsOptions: [],
        currencyOptions: [],
        countryOptions: [],
      } as {
        nationalityOptions: string[];
        employeeStatusOptions: string[];
        employmentTypeOptions: string[];
        employmentStatusOptions: string[];
        jobGradeOptions: string[];
        reasonOfFixedTermsOptions: string[];
        currencyOptions: string[];
        countryOptions: string[];
      },
    );
  }, [rows]);

  const { columnConfigs, columnGroupingModel } = useMemo(() => {
    const {
      countryEnums,
      employeeStatusEnums,
      positionEnums,
      employmentTypeEnums,
      employmentStatusEnums,
      jobGradeEnums,
      personnelGroupNames,
      personnelGroupEnumsByGroup,
      workingHoursTypeEnums,
      salaryTypeEnums,
      salaryClassEnums,
      salaryChangeReasonEnums,
      allowanceTypeEnums,
      benefitTypeEnums,
      eventFrequencyEnums,
      fixedTermReasonEnums,
      customEntityEmploymentDetailsEnums,
    } = reportEnums;

    const {
      nationalityOptions,
      employeeStatusOptions,
      employmentTypeOptions,
      employmentStatusOptions,
      jobGradeOptions,
      reasonOfFixedTermsOptions,
      currencyOptions,
      countryOptions,
    } = dataColumnOptions;

    const showDateOfBirthAndAgeColumns =
      aMyHRUnits.length > 0 || aMyManUnits.length > 0 || aMyAltUnits.length > 0;

    const isEndedEmployee = (row: PersonnelListCustomReportRow_Employee) =>
      isAccessToEndedEmployeeRestrictedByEmploymentStatus({
        endDate: row.endDate,
        hasEndedStatus: row.status === 'ENDED',
      });

    const employeeColumnConfigs: GridColDef<PersonnelListCustomReportRow_Employee>[] =
      [
        {
          field: 'employeeId',
          headerName: t('laPicture'),
          filterable: false,
          sortable: false,
          disableExport: true,
          renderCell: (params) => {
            return (
              <EmployeeCellComponent
                {...params}
                pictureOnly={true}
                onClick={() => {
                  goToEmployeeBasicDataView(params.row.employeeId);
                }}
                disabled={isEndedEmployee(params.row)}
              />
            );
          },
          minWidth: 60,
        },
        {
          field: 'firstName',
          headerName: t('laFirstName'),
          hideable: false,
          renderCell: (params) => {
            return (
              <ButtonCellComponent
                {...params}
                onClick={() => {
                  goToEmployeeBasicDataView(params.row.employeeId);
                }}
                disabled={isEndedEmployee(params.row)}
              />
            );
          },
          minWidth: 90,
        },
        {
          field: 'lastName',
          headerName: t('laLastName'),
          hideable: false,
          renderCell: (params) => {
            return (
              <ButtonCellComponent
                {...params}
                onClick={() => {
                  goToEmployeeBasicDataView(params.row.employeeId);
                }}
                disabled={isEndedEmployee(params.row)}
              />
            );
          },
          minWidth: 110,
        },
        {
          field: 'employeeNumber',
          headerName: t('laEmpId'),
          minWidth: 130,
        },
        {
          field: 'contractNumber',
          headerName: t('fEmpContractNumber'),
          minWidth: 150,
        },
        ...conditionalGridColDefs<PersonnelListCustomReportRow_Employee>(
          showDateOfBirthAndAgeColumns,
          [
            {
              field: 'dateOfBirth',
              type: 'date',
              headerName: t('laDoB'),
              valueFormatter: dateCellValueFormatter,
            },
            ...conditionalGridColDefs<PersonnelListCustomReportRow_Employee>(
              !isReportDateToday,
              [
                {
                  field: 'age',
                  type: 'number',
                  headerName: `${t('age')} (${moment(reportDate).format('L')})`,
                  minWidth: 140,
                },
              ],
            ),
            {
              field: 'currentAge',
              type: 'number',
              headerName: t('currentAge'),
            },
          ],
        ),
        {
          field: 'gender',
          headerName: t('laGender'),
          type: 'singleSelect',
          valueGetter: (value: string | undefined) =>
            value
              ? value
                  .replace('M', t('laMale'))
                  .replace('F', t('laFemale'))
                  .replace('O', t('laOther'))
              : '',
          valueOptions: [t('laMale'), t('laFemale'), t('laOther')],
        },
        {
          field: 'nationality',
          type: 'singleSelect',
          headerName: t('laNationality'),
          valueGetter: (value) => getEnumName(countryEnums, value),
          valueOptions: getSingleSelectColumnEnumValueOptions(
            countryEnums,
            nationalityOptions,
          ),
        },
        {
          field: 'originalStartDate',
          type: 'date',
          headerName: t('laEmpOrigStartDate'),
          valueFormatter: dateCellValueFormatter,
        },
        {
          field: 'startDate',
          type: 'date',
          headerName: t('laStartDate'),
          valueFormatter: dateCellValueFormatter,
        },
        {
          field: 'endDate',
          type: 'date',
          headerName: t('laEndDate'),
          valueFormatter: dateCellValueFormatter,
        },
        {
          field: 'status',
          type: 'singleSelect',
          headerName: t('fEmpStatus'),
          valueGetter: (value) => getEnumName(employeeStatusEnums, value),
          valueOptions: getSingleSelectColumnEnumValueOptions(
            employeeStatusEnums,
            employeeStatusOptions,
          ),
        },
        { field: 'bankAccount', headerName: t('laBankAccount'), minWidth: 180 },
      ];

    const employmentColumnConfigs: GridColDef<PersonnelListCustomReportRow_Employment>[] =
      [
        {
          field: 'vacationBalanceCalculation',
          type: 'number',
          headerName: t('laVacBalanceCal'),
          renderCell: ({ value }) => `${value} ${t('laDays')}`,
        },
        {
          field: 'currency',
          type: 'singleSelect',
          headerName: t('laCurrency'),
          valueOptions: currencyOptions,
        },
        {
          field: 'employmentType',
          type: 'singleSelect',
          headerName: t('laEmploymentType'),
          valueGetter: (value) => getEnumName(employmentTypeEnums, value),
          valueOptions: getSingleSelectColumnEnumValueOptions(
            employmentTypeEnums,
            employmentTypeOptions,
          ),
        },
        {
          field: 'employmentStatus',
          type: 'singleSelect',
          headerName: t('laEmploymentStatus'),
          valueGetter: (value) => getEnumName(employmentStatusEnums, value),
          valueOptions: getSingleSelectColumnEnumValueOptions(
            employmentStatusEnums,
            employmentStatusOptions,
          ),
        },
        {
          field: 'jobGrade',
          type: 'singleSelect',
          headerName: t('laJobGrade'),
          valueGetter: (value) => getEnumName(jobGradeEnums, value),
          valueOptions: getSingleSelectColumnEnumValueOptions(
            jobGradeEnums,
            jobGradeOptions,
          ),
        },
        {
          field: 'trialPeriod',
          headerName: t('laTrialPeriod'),
          valueGetter: (value) =>
            !!value ? getTrialPeriodOptionName(value) : '',
        },
        {
          field: 'trialPeriodEndDate',
          type: 'date',
          headerName: t('laTrialPeriodEndDate'),
          valueFormatter: dateCellValueFormatter,
        },
        {
          field: 'plannedEndDate',
          type: 'date',
          headerName: t('fEmplPlannedEndDate'),
          valueFormatter: dateCellValueFormatter,
        },
        {
          field: 'reasonOfFixedTerms',
          type: 'singleSelect',
          headerName: t('laReasonsFixTerm'),
          valueGetter: (value) => getEnumName(fixedTermReasonEnums, value),
          valueOptions: getSingleSelectColumnEnumValueOptions(
            fixedTermReasonEnums,
            reasonOfFixedTermsOptions,
          ),
        },
      ];

    const personnelGroupColumnConfigs: GridColDef<PersonnelListCustomReportRow_PersonnelGroups>[] =
      [
        EnumListKey.PERSONNEL_GROUP_1,
        EnumListKey.PERSONNEL_GROUP_2,
        EnumListKey.PERSONNEL_GROUP_3,
        EnumListKey.PERSONNEL_GROUP_4,
        EnumListKey.PERSONNEL_GROUP_5,
      ].map((key, index) => {
        const pgEnums = personnelGroupEnumsByGroup[key] ?? [];

        return {
          field: `personnelGroup${index + 1}`,
          headerName: getEnumName(personnelGroupNames, key),
          valueGetter: (value) => getEnumName(pgEnums, value),
          type: 'singleSelect',
          valueOptions: getSingleSelectColumnEnumValueOptions(pgEnums),
          flex: 1,
          minWidth: 140,
        };
      });

    const getAdditionalPersonnelGroupColumnConfig = (enums: Enum[]) => {
      const resolveColumnType = (fieldType: string | undefined) => {
        switch (fieldType) {
          case 'DATE':
            return 'date';
          case 'BOOLEAN':
            return 'boolean';
          case 'NUMBER':
            return 'number';
          case 'ENUM':
            return 'singleSelect';
          default:
            return 'string';
        }
      };

      const resolveMinWidth = (fieldType: string | undefined) => {
        switch (fieldType) {
          case 'BOOLEAN':
          case 'DATE':
          case 'NUMBER':
            return 100;
          case 'MULTISELECT':
            return 200;
          case 'ENUM':
          case 'TEXT':
          case 'STRING':
          default:
            return 150;
        }
      };

      const resolveAdditionalColumnProps = (
        code: string,
        fieldType: string | undefined,
      ) => {
        switch (fieldType) {
          case 'ENUM':
            return {
              valueOptions: getCustomDataFieldEnums(code).map((e) => e.name),
            };
          case 'BOOLEAN':
            return {
              renderCell: BooleanCellComponent,
            };
          default:
            return {};
        }
      };

      return enums
        .map((enumData): GridColDef<PersonnelListCustomReportRow>[] => {
          const { code, name } = enumData;

          const fieldType = getCustomDataFieldDataType(code);

          return [
            {
              field: `additional-personnel-groups-${code}`,
              type: resolveColumnType(fieldType),
              headerName: name,
              valueGetter: (_value, row) => {
                const arrayData = row.additionalPersonnelGroupsArray;
                const foundEntry = arrayData.find(
                  (entry) => entry.code === code,
                );

                // Convert value based on the type of customdata field type
                if (!!foundEntry) {
                  const { value } = foundEntry;

                  switch (fieldType) {
                    case 'BOOLEAN':
                      return value === 'true' ? true : '';
                    case 'DATE':
                      return convertToDateOrUndefined(value as string);
                    case 'ENUM':
                      return (
                        getEnumObject(
                          getCustomDataFieldEnums(code),
                          value as string,
                        )?.name ?? value
                      );
                    case 'MULTISELECT':
                      return (value as string[])
                        .map(
                          (item) =>
                            getEnumObject(getCustomDataFieldEnums(code), item)
                              ?.name ?? item,
                        )
                        .join(', ');
                    case 'NUMBER':
                      return parseInt(value as string);
                    case 'TEXT':
                    case 'STRING':
                    default:
                      return value;
                  }
                }
                return '';
              },
              valueFormatter: (value) => {
                switch (fieldType) {
                  case 'DATE':
                    return getFormattedDateString(value, 'L');
                  default:
                    return value;
                }
              },
              minWidth: resolveMinWidth(fieldType),
              ...resolveAdditionalColumnProps(code, fieldType),
            },
          ];
        })
        .flat();
    };

    const additionalPersonnelGroupColumnConfigs: GridColDef<PersonnelListCustomReportRow>[] =
      getAdditionalPersonnelGroupColumnConfig(
        customEntityEmploymentDetailsEnums,
      );

    const formatAdditionalCostCenter = (
      costCenterId: number,
      costCenterPercent?: number,
    ) => {
      const costCenterName =
        getCostCenterById(costCenterId)?.fCostCenterName ?? t('laUnknown');

      return typeof costCenterPercent !== 'undefined'
        ? `${costCenterName} (${costCenterPercent}%)`
        : costCenterName;
    };

    const getAdditionalCostCenterValues = (value: AdditionalCostCenter[]) =>
      !!value
        ? value
            .filter((cc) => !cc.costCenterIsDefault)
            .map(({ costCenterId, costCenterPercent }) =>
              formatAdditionalCostCenter(costCenterId, costCenterPercent),
            )
            .join(', ')
        : '';

    const renderAdditionalCostCenterValue = (value: AdditionalCostCenter[]) => {
      return !!value ? (
        <Box>
          {value
            .filter((cc) => !cc.costCenterIsDefault)
            .map(({ costCenterId, costCenterPercent }, index) => {
              return (
                <Box key={`compensation${index}`}>
                  {formatAdditionalCostCenter(costCenterId, costCenterPercent)}
                </Box>
              );
            })}
        </Box>
      ) : null;
    };

    const positionColumnConfigs: GridColDef<PersonnelListCustomReportRow_Position>[] =
      [
        { field: 'jobTitle', headerName: t('fEmpJobTitle'), minWidth: 130 },
        {
          field: 'organizationUnit',
          headerName: t('laOrgUnit'),
          minWidth: 180,
        },
        { field: 'organizationUnitNumber', headerName: t('laUnitNumber') },
        {
          field: 'parentUnit',
          headerName: t('fOrgUnitParentUnit'),
        },
        {
          field: 'position',
          type: 'singleSelect',
          headerName: t('laPosition'),
          valueGetter: (value) => getEnumName(positionEnums, value),
          valueOptions: getSingleSelectColumnEnumValueOptions(positionEnums),
          minWidth: 150,
        },
        {
          field: 'manager',
          headerName: t('laManager'),
          minWidth: 150,
        },
        {
          field: 'managerNumber',
          headerName: t('laManagerNumber'),
          minWidth: 150,
        },
        {
          field: 'alternateManager',
          headerName: t('laAltManager'),
          valueGetter: (value) => getEmployeeNames(value ?? []),
          minWidth: 150,
        },
        {
          field: 'deputyManager',
          headerName: t('laDeputyManager'),
          valueGetter: (value) => getEmployeeNames(value ?? []),
          minWidth: 150,
        },

        { field: 'legalEntity', headerName: t('laLegalEntity'), minWidth: 210 },
        { field: 'costCenter', headerName: t('laCostCenter'), minWidth: 210 },
        {
          field: 'costCenterCode',
          headerName: t('laCostCenterCode'),
          minWidth: 180,
        },
        {
          field: 'additionalCostCenters',
          headerName: t('fEmpAddlCostCenters'),
          valueGetter: (_value, row) =>
            getAdditionalCostCenterValues(row.additionalCostCentersArray),
          renderCell: ({ row }) =>
            renderAdditionalCostCenterValue(row.additionalCostCentersArray),
          minWidth: 250,
        },
        {
          field: 'country',
          type: 'singleSelect',
          headerName: t('laCountry'),
          valueGetter: (value) => getEnumName(countryEnums, value),
          valueOptions: getSingleSelectColumnEnumValueOptions(
            countryEnums,
            countryOptions,
          ),
        },
      ];

    const workingHoursColumnConfigs: GridColDef<PersonnelListCustomReportRow_Contact>[] =
      [
        {
          field: 'workingHoursType',
          type: 'singleSelect',
          headerName: t('laWorkingHoursType'),
          valueGetter: (value) => getEnumName(workingHoursTypeEnums, value),
          valueOptions: getSingleSelectColumnEnumValueOptions(
            workingHoursTypeEnums,
          ),
        },
        {
          field: 'fullTimeHours',
          type: 'number',
          headerName: t('laFullTimeHours'),
        },
        {
          field: 'partTimeHours',
          type: 'number',
          headerName: t('laPartTimeHours'),
        },
        {
          field: 'partTimePercentage',
          type: 'number',
          headerName: t('laWorkHoursPercentage'),
          renderCell: ({ value }) => {
            const percentage = parseInt(value);
            return isNaN(percentage) ? '' : `${value} %`;
          },
        },
      ];

    const salariesColumnConfigs: GridColDef<PersonnelListCustomReportRow>[] = [
      {
        field: 'salaryType',
        type: 'singleSelect',
        headerName: t('laSalaryType'),
        valueGetter: (value) => getEnumName(salaryTypeEnums, value),
        valueOptions: getSingleSelectColumnEnumValueOptions(salaryTypeEnums),
      },
      {
        field: 'salaryClass',
        type: 'singleSelect',
        headerName: t('laSalaryClass'),
        valueGetter: (value) => getEnumName(salaryClassEnums, value),
        valueOptions: getSingleSelectColumnEnumValueOptions(salaryClassEnums),
      },
      {
        field: 'changeReason',
        type: 'singleSelect',
        headerName: t('laSalaryReason'),
        valueGetter: (value) => getEnumName(salaryChangeReasonEnums, value),
        valueOptions: getSingleSelectColumnEnumValueOptions(
          salaryChangeReasonEnums,
        ),
      },
      {
        field: 'baseSalary',
        type: 'number',
        headerName: t('laBaseSalary'),
        renderCell: ({ value, row }) => `${value} ${row.currency}`,
      },
      {
        field: 'salaryFrequency',
        type: 'singleSelect',
        headerName: t('laBaseSalaryFrequency'),
        valueGetter: (value) => getEnumName(eventFrequencyEnums, value),
        valueOptions:
          getSingleSelectColumnEnumValueOptions(eventFrequencyEnums),
      },
    ];

    const getCompensationColumnConfig = (
      category: 'allowance' | 'benefits',
      enums: Enum[],
    ) => {
      const resolveValueGetter =
        (arrayName: keyof PersonnelListCustomReportRow, type: string) =>
        (getValue: (data: CompensationEntry) => unknown) =>
        (_value: never, row: PersonnelListCustomReportRow) => {
          if (['allowanceArray', 'benefitsArray'].includes(arrayName)) {
            const arrayData = (row[arrayName] ?? []) as CompensationEntry[];

            const foundEntry = arrayData.find((entry) => entry.type === type);

            return !!foundEntry ? getValue(foundEntry) : '';
          }

          return '';
        };

      return enums
        .map(({ code }): GridColDef<PersonnelListCustomReportRow>[] => {
          const compensationValueGetter = resolveValueGetter(
            `${category}Array`,
            code,
          );

          return [
            {
              field: `${category}-${code}-amount`,
              type: 'number',
              headerName: t('laAmount'),
              valueGetter: compensationValueGetter((entry) => entry.amount),
              renderCell: ({ value, row }) =>
                !!value ? `${value} ${row.currency}` : '',
            },
            {
              field: `${category}-${code}-frequency`,
              type: 'singleSelect',
              headerName: t('laFrequency'),
              valueGetter: compensationValueGetter((entry) => {
                return eventFrequencyEnums.find(
                  (e) => e.code === entry.frequency,
                )?.name;
              }),
              valueOptions:
                getSingleSelectColumnEnumValueOptions(eventFrequencyEnums),
            },
          ];
        })
        .flat();
    };

    const getCompensationColumnGrouping = (category: string, enums: Enum[]) => {
      return enums
        .map(({ code, name }) => {
          return {
            groupId: `${code}`,
            headerName: `${name}`,
            children: [
              {
                field: `${category}-${code}-amount`,
              },
              {
                field: `${category}-${code}-frequency`,
              },
            ],
          };
        })
        .flat();
    };

    const allowanceColumnConfigs: GridColDef<PersonnelListCustomReportRow>[] =
      getCompensationColumnConfig('allowance', allowanceTypeEnums);
    const benefitsColumnConfigs: GridColDef<PersonnelListCustomReportRow>[] =
      getCompensationColumnConfig('benefits', benefitTypeEnums);

    const contactColumnConfigs: GridColDef<PersonnelListCustomReportRow_Contact>[] =
      [
        { field: 'office', headerName: t('laOffice') },
        { field: 'phone', headerName: t('fEmpPhoneWork'), minWidth: 150 },
        { field: 'mobilePhone', headerName: t('fEmpPhoneMob'), minWidth: 150 },
        {
          field: 'email',
          headerName: t('laEmail'),
          minWidth: 250,
          width: columnWidths.email,
        },
      ];

    const secondaryTreeColumnConfigs: GridColDef<PersonnelListCustomReportRow_SecondaryTree>[] =
      [
        { field: 'secondaryUnit', headerName: t('laSecTreeOrgUnit') },
        { field: 'secondaryUnitNumber', headerName: t('laSecTreeUnitNumber') },
      ];

    const accountDetailsColumnConfigs: GridColDef<PersonnelListCustomReportRow_AccountDetails>[] =
      [
        {
          field: 'userName',
          headerName: t('laUsername'),
          minWidth: 160,
        },
        {
          field: 'userLanguage',
          headerName: t('laLanguage'),
          minWidth: 130,
          valueGetter: (value) => getLanguageTranslation(value),
        },
        {
          field: 'roles',
          headerName: t('table_col_roles'),
          minWidth: 130,
          width: columnWidths.roles,
        },
        {
          field: 'loginMethodSolaforce',
          headerName: t('loginMethod'),
          valueGetter: (value) => {
            if (typeof value === 'undefined') {
              return '';
            }

            return !!value ? 'Solaforce' : t('laOther');
          },
          minWidth: 130,
        },
        {
          field: 'userStatus',
          headerName: t('laStatus'),
          valueGetter: (value) => {
            switch (value) {
              case 'ACTIVE':
                return t('laActive');
              case 'DISABLED':
                return t('laDisabled');
              case 'RESET':
                return t('statusResetPassword');
              default:
                return value;
            }
          },
          minWidth: 130,
        },
      ];

    const miscColumnConfigs: GridColDef<PersonnelListCustomReportRow_Misc>[] =
      [];

    const {
      workingHoursDataEnabled,
      salariesDataEnabled,
      totalCompensationDataEnabled,
      additionalPersonnelGroupsDataEnabled,
      additionalTreeDataEnabled,
      accountDetailsDataEnabled,
    } = enabledOptionalDataSections;

    const columnConfigs = [
      ...employeeColumnConfigs,
      ...positionColumnConfigs,
      ...employmentColumnConfigs,
      ...personnelGroupColumnConfigs,
      ...(additionalPersonnelGroupsDataEnabled
        ? additionalPersonnelGroupColumnConfigs
        : []),
      ...contactColumnConfigs,
      ...miscColumnConfigs,
      ...(salariesDataEnabled ? salariesColumnConfigs : []),
      ...(totalCompensationDataEnabled
        ? [...allowanceColumnConfigs, ...benefitsColumnConfigs]
        : []),
      ...(workingHoursDataEnabled ? workingHoursColumnConfigs : []),
      ...(additionalTreeDataEnabled ? secondaryTreeColumnConfigs : []),
      ...(accountDetailsDataEnabled ? accountDetailsColumnConfigs : []),
    ] as GridColDef<PersonnelListCustomReportRow>[];

    const columnGroupingModel: GridColumnGroupingModel = [
      {
        groupId: 'employee',
        headerName: t('laEmployee'),
        children: getFieldNamesFromColumnConfig(employeeColumnConfigs),
      },
      {
        groupId: 'employment',
        headerName: t('laEmployment'),
        children: getFieldNamesFromColumnConfig(employmentColumnConfigs),
      },
      {
        groupId: 'personnelGroups',
        headerName: t('laPersonnelGroups'),
        children: getFieldNamesFromColumnConfig(personnelGroupColumnConfigs),
      },
      {
        groupId: 'additionalPersonnelGroups',
        headerName: t('additionalPersonnelGroups'),
        children: getFieldNamesFromColumnConfig(
          additionalPersonnelGroupColumnConfigs,
        ),
      },
      {
        groupId: 'position',
        headerName: t('laPosition'),
        children: getFieldNamesFromColumnConfig(positionColumnConfigs),
      },
      {
        groupId: ' ',
        children: getFieldNamesFromColumnConfig(miscColumnConfigs),
      },
      {
        groupId: 'salary',
        headerName: t('laBaseSalary'),
        children: getFieldNamesFromColumnConfig(salariesColumnConfigs),
      },
      {
        groupId: 'allowance',
        headerName: t('laAllowance'),
        children: getCompensationColumnGrouping(
          'allowance',
          allowanceTypeEnums,
        ),
      },
      {
        groupId: 'benefits',
        headerName: t('laBenefits'),
        children: getCompensationColumnGrouping('benefits', benefitTypeEnums),
      },
      {
        groupId: 'workingHours',
        headerName: t('laWorkingHours'),
        children: getFieldNamesFromColumnConfig(workingHoursColumnConfigs),
      },
      {
        groupId: 'secondaryTree',
        headerName: t('secondaryTree'),
        children: getFieldNamesFromColumnConfig(secondaryTreeColumnConfigs),
      },
      {
        groupId: 'contact',
        headerName: t('laContact'),
        children: getFieldNamesFromColumnConfig(contactColumnConfigs),
      },
      {
        groupId: 'accountDetails',
        headerName: t('userAccountDetails'),
        children: getFieldNamesFromColumnConfig(accountDetailsColumnConfigs),
      },
    ];

    return {
      columnConfigs,
      columnGroupingModel,
    };
  }, [
    t,
    reportDate,
    isReportDateToday,
    reportEnums,
    getEmployeeNames,
    getCostCenterById,
    getLanguageTranslation,
    getTrialPeriodOptionName,
    getCustomDataFieldDataType,
    getCustomDataFieldEnums,
    enabledOptionalDataSections,
    aMyHRUnits,
    aMyManUnits,
    aMyAltUnits,
    isAccessToEndedEmployeeRestrictedByEmploymentStatus,
    goToEmployeeBasicDataView,
    dataColumnOptions,
    columnWidths,
  ]);

  return {
    isLoading: isLoadingEmploymentDetailsEntityAttributes,
    table: {
      columnConfigs,
      rows,
      columnGroupingModel,
    },
  };
};
